import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import Login from "@/pages/Login";
import Settings from "@/pages/Settings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

// NavigateKeepQuery — drop-in <Navigate /> that preserves the current
// URL's ?query string on the target. Used by the auth-redirect routes
// so a click on the sidebar (which encodes the view in ?view=) survives
// the intermediate <Navigate to="/" /> and <Navigate to="/dashboard" />
// bounces that React Router performs during auth refresh / Strict Mode.
//
// The OAuth post-success navigates ('/dashboard' after fetchUser) are
// kept on the plain Navigate / navigate() so they continue to strip
// the ?user_id= callback param on purpose.
const NavigateKeepQuery = ({ to, replace }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search || ""}`} replace={replace} />;
};

// Auth hook
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async (userId) => {
    try {
      // Try cookie-based auth first, then fall back to userId query
      const response = await axios.get(`${API}/auth/me${userId ? `?user_id=${userId}` : ''}`, {
        withCredentials: true
      });
      const userData = { ...response.data, id: response.data.id || userId };
      setUser(userData);
      if (userId) {
        localStorage.setItem('user_id', userId);
      }
      return true;
    } catch (e) {
      console.error("Failed to fetch user:", e);
      localStorage.removeItem('user_id');
      setUser(null);
      return false;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // First try cookie-based auth
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data && response.data.id) {
          setUser(response.data);
          setLoading(false);
          return;
        }
      } catch (e) {
        // Cookie auth failed, try localStorage
      }
      
      // Fall back to localStorage userId (for Google OAuth)
      const userId = localStorage.getItem('user_id');
      if (userId) {
        await fetchUser(userId);
      }
      setLoading(false);
    };
    initAuth();
  }, [fetchUser]);

  const login = async () => {
    try {
      // Clear any existing user data before OAuth to prevent conflicts
      localStorage.removeItem('user_id');
      
      const response = await axios.get(`${API}/auth/google/login`);
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      } else {
        toast.error("Failed to initiate Google login");
      }
    } catch (e) {
      const message = e.response?.data?.detail || "Google OAuth not configured";
      toast.error(message);
      console.error("Login error:", e);
    }
  };

  const loginDemo = async () => {
    try {
      const response = await axios.post(`${API}/auth/demo`);
      if (response.data.id) {
        localStorage.setItem('user_id', response.data.id);
        await fetchUser(response.data.id);
        toast.success("Welcome to RailSync Demo!");
        return true;
      }
      return false;
    } catch (e) {
      toast.error("Failed to create demo session");
      console.error("Demo login error:", e);
      return false;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem('user_id');
    // Clear cookies by setting expired
    document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    setUser(null);
    toast.success("Logged out successfully");
  };

  return { user, setUser, loading, login, loginDemo, logout, fetchUser };
};

// OAuth callback handler
const OAuthCallback = ({ fetchUser }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      const userId = searchParams.get('user_id');
      const error = searchParams.get('error');

      if (error) {
        toast.error("Authentication failed. Please try again.");
        navigate('/', { replace: true });
      } else if (userId) {
        localStorage.setItem('user_id', userId);
        const success = await fetchUser(userId);
        if (success) {
          toast.success("Welcome! You're now logged in.");
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };
    handleCallback();
  }, [searchParams, navigate, fetchUser]);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="text-zinc-400">Processing authentication...</div>
    </div>
  );
};

// Dashboard wrapper that handles OAuth callback
const DashboardWrapper = ({ user, loading, onLogout, fetchUser }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const handleAuth = async () => {
      const userId = searchParams.get('user_id');
      const error = searchParams.get('error');

      if (error) {
        toast.error("Authentication failed. Please try again.");
        navigate('/', { replace: true });
        return;
      }

      if (userId && !user) {
        // Coming from OAuth callback
        localStorage.setItem('user_id', userId);
        const success = await fetchUser(userId);
        if (success) {
          toast.success("Welcome! You're now logged in with Google.");
          // Remove query params from URL — intentional after OAuth.
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
        return;
      }

      setProcessing(false);
    };
    handleAuth();
  }, [searchParams, navigate, fetchUser, user]);

  if (processing && searchParams.get('user_id')) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-400">Processing authentication...</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full spinner" />
      </div>
    );
  }
  
  if (!user) {
    // NavigateKeepQuery preserves ?view= through the redirect, so when
    // user state resolves and Route(/) bounces back to /dashboard, the
    // ?view= the user came in on is still attached.
    return <NavigateKeepQuery to="/" replace />;
  }

  return <Dashboard user={user} onLogout={onLogout} />;
};

// Protected route wrapper
const ProtectedRoute = ({ children, user, loading }) => {
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <NavigateKeepQuery to="/" replace />;
  }

  return children;
};

function App() {
  const { user, setUser, loading, login, loginDemo, logout, fetchUser } = useAuth();

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              user ? (
                <NavigateKeepQuery to="/dashboard" replace />
              ) : (
                <LandingPage onLogin={login} onDemoLogin={loginDemo} />
              )
            }
          />
          <Route
            path="/login"
            element={
              user ? (
                <NavigateKeepQuery to="/dashboard" replace />
              ) : (
                <Login setUser={setUser} />
              )
            }
          />
          <Route 
            path="/dashboard" 
            element={
              <DashboardWrapper 
                user={user} 
                loading={loading} 
                onLogout={logout} 
                fetchUser={fetchUser}
              />
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute user={user} loading={loading}>
                <Settings user={user} setUser={setUser} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/callback" 
            element={<OAuthCallback fetchUser={fetchUser} />} 
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
